import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, MapPin, Users, FileText, Music2, Video, Link2, Clock, Music4, FolderOpen, CalendarRange } from "lucide-react";
import api, { API, withToken, eventVisualUrl } from "@/lib/api";
import AvailabilityButtons from "@/components/AvailabilityButtons";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { dateLocale } from "@/lib/i18n";
import { FeeDueBanner } from "@/components/MyFeesCard";

const MAIN_CLS = {
  repetition: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  prestation: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  autre: "bg-sky-500/15 text-sky-400 border-sky-500/30",
};
const LEGACY_MAIN = { concert: "prestation", concours: "prestation", evenement: "autre" };
const mainOf = (k) => (MAIN_CLS[k] ? k : LEGACY_MAIN[k] || "autre");
const chipLabel = (e, t) => e.subcategory || t("cat." + mainOf(e.category));
const chipCls = (e) => MAIN_CLS[mainOf(e.category)];
const KIND_ICONS = { partition: FileText, audio: Music2, video: Video, link: Link2 };

export const fmtDate = (d, lang = "fr") =>
  new Date(d + "T00:00").toLocaleDateString(dateLocale(lang), { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const fmtShort = (d, lang = "fr") =>
  new Date(d + "T00:00").toLocaleDateString(dateLocale(lang), { weekday: "short", day: "numeric", month: "short" });

export default function DashboardPage() {
  const { t, lang } = useAuth();
  const [next, setNext] = useState(null);
  const [recent, setRecent] = useState([]);
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const navigate = useNavigate();

  const loadNext = () => api.get("/events/next").then((r) => setNext(r.data)).catch(() => {});

  useEffect(() => {
    loadNext();
    api.get("/recent").then((r) => setRecent(r.data)).catch(() => {});
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/events").then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      setUpcoming(r.data.filter((e) => e.date >= today).slice(0, 5));
    }).catch(() => {});
  }, []);

  const answer = async (status) => {
    try {
      await api.post(`/events/${next.event.id}/availability`, { status });
      toast.success(t("common.availability_saved"));
      loadNext();
    } catch (e) {
      toast.error(e.friendly);
    }
  };

  const openRecent = (f) => {
    if (f.context === "documents") navigate(`/documents?folder=${f.folder_id}&open=${f.file_id}`);
    else navigate(`/bibliotheque?node=${f.node_id}&open=${f.file_id}`);
  };

  const ev = next?.event;
  const availPct = next?.total_members ? Math.floor((next.available_count / next.total_members) * 100) : 0;
  const statCards = [
    { key: "members", label: t("dash.stat_members"), value: stats?.members, icon: Users, tid: "stat-members" },
    { key: "pieces", label: t("dash.stat_pieces"), value: stats?.pieces, icon: Music4, tid: "stat-pieces" },
    { key: "documents", label: t("dash.stat_documents"), value: stats?.documents, icon: FolderOpen, tid: "stat-documents" },
    { key: "upcoming_events", label: t("dash.stat_upcoming"), value: stats?.upcoming_events, icon: CalendarRange, tid: "stat-upcoming-events" },
  ];

  return (
    <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-rows-[auto_auto_minmax(0,1fr)] lg:gap-4 lg:flex-1 lg:min-h-0" data-testid="dashboard-page">
      <div className="hidden lg:grid lg:grid-cols-4 gap-3" data-testid="dashboard-stats">
        {statCards.map((s) => (
          <div key={s.key} className="app-card p-3 flex items-center gap-3 anim-fade-up" data-testid={s.tid}>
            <s.icon className="h-6 w-6 brand shrink-0" />
            <div className="min-w-0">
              <p className="font-heading text-2xl font-bold leading-tight" data-testid={`${s.tid}-value`}>{s.value ?? "—"}</p>
              <p className="text-xs t-secondary uppercase tracking-wider leading-tight">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <section
        className={`app-card relative overflow-x-hidden lg:overflow-y-auto p-4 lg:p-5 anim-fade-up lg:max-h-[42vh] ${ev ? "cursor-pointer hover:bg-white/[0.03] transition-colors" : ""}`}
        data-testid="next-event-card"
        {...(ev ? {
          role: "button",
          tabIndex: 0,
          onClick: () => navigate(`/agenda/${ev.id}`),
          onKeyDown: (e) => { if (e.key === "Enter" && e.target === e.currentTarget) navigate(`/agenda/${ev.id}`); },
        } : {})}
      >
        {ev && eventVisualUrl(ev) && (
          <img
            src={eventVisualUrl(ev)}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover opacity-15 pointer-events-none"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            data-testid="next-event-bg-image"
          />
        )}
        <div className="relative">
          <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 brand" /> {t("dash.next_event")}
          </h2>
          {ev ? (
            <div className="space-y-2.5 lg:space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${chipCls(ev)}`} data-testid="next-event-category">
                  {chipLabel(ev, t)}
                </span>
                <h3 className="font-heading text-xl lg:text-2xl font-bold" data-testid="next-event-title">{ev.title}</h3>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm t-secondary">
                <span className="flex items-center gap-2" data-testid="next-event-date">
                  <Clock className="h-4 w-4" /> {fmtDate(ev.date, lang)}
                  {ev.start_time && ` · ${ev.start_time}`}{ev.end_time && ` – ${ev.end_time}`}
                </span>
                {ev.location && (
                  <span className="flex items-center gap-2" data-testid="next-event-location"><MapPin className="h-4 w-4" /> {ev.location}</span>
                )}
                <span className="flex items-center gap-2" data-testid="next-event-available-count">
                  <Users className="h-4 w-4" /> {next.available_count} {t("dash.members_available")} / {next.total_members}
                  <span className="font-bold accent" data-testid="next-event-available-pct">· {availPct}%</span>
                </span>
              </div>
              <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <p className="text-xs uppercase tracking-wider t-secondary mb-2">{t("dash.my_availability")}</p>
                <AvailabilityButtons value={next.my_status} onChange={answer} testidPrefix="dashboard-availability" />
              </div>
              {(next.program || []).length > 0 && (
                <div className="border-t border-app pt-2.5 lg:pt-3" data-testid="next-event-program">
                  <p className="text-xs uppercase tracking-wider t-secondary mb-1.5 lg:mb-2">{t("dash.program")}</p>
                  <div className="space-y-2 max-h-32 lg:max-h-none overflow-y-auto">
                    {(() => {
                      const blocks = [];
                      next.program.forEach((p) => {
                        const axis = p.axis || t("dash.program");
                        let b = blocks.find((x) => x.axis === axis);
                        if (!b) { b = { axis, items: [] }; blocks.push(b); }
                        b.items.push(p);
                      });
                      return blocks.map((b) => (
                        <div key={b.axis}>
                          <p className="text-xs uppercase tracking-wider accent font-bold mb-0.5">{b.axis}</p>
                          <ol className="space-y-0.5">
                            {b.items.map((p, j) => (
                              <li key={j} className="text-sm">
                                <span className="t-secondary mr-2">{j + 1}.</span>
                                <button className="brand hover:underline" onClick={(e) => { e.stopPropagation(); navigate(`/bibliotheque?node=${p.node_id}`); }} data-testid={`next-event-program-piece-${b.axis}-${j}`}>
                                  {p.name}
                                </button>
                                {p.note && <span className="block text-xs t-secondary italic ml-5">→ {p.note}</span>}
                              </li>
                            ))}
                          </ol>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="t-secondary text-sm" data-testid="no-upcoming-event">{t("dash.no_upcoming")}</p>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start lg:items-stretch lg:min-h-0">
        <section className="app-card p-5 anim-fade-up-1 hidden lg:flex lg:h-full lg:flex-col lg:min-h-0" data-testid="upcoming-events-card">
          <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
            <CalendarRange className="h-5 w-5 brand" /> {t("dash.upcoming_title")}
          </h2>
          {upcoming.length === 0 ? (
            <p className="t-secondary text-sm" data-testid="no-upcoming-events">{t("dash.no_upcoming")}</p>
          ) : (
            <ul className="divide-y divide-[var(--border-color)] max-h-64 lg:max-h-none lg:flex-1 lg:min-h-0 overflow-y-auto">
              {upcoming.map((e) => (
                <li key={e.id}>
                  <button
                    onClick={() => navigate("/agenda")}
                    className="w-full flex items-center gap-3 py-3 px-2 rounded-lg hover:bg-white/5 transition-colors text-left"
                    data-testid={`upcoming-event-${e.id}`}
                  >
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border shrink-0 ${chipCls(e)}`}>
                      {chipLabel(e, t)}
                    </span>
                    <span className="text-sm font-medium truncate flex-1">{e.title}</span>
                    <span className="text-xs t-secondary whitespace-nowrap">
                      {fmtShort(e.date, lang)}{e.start_time && ` · ${e.start_time}`}
                    </span>
                    {e.location && (
                      <span className="text-xs t-secondary hidden md:flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.location}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="app-card p-4 lg:p-5 anim-fade-up-2 lg:h-full lg:flex lg:flex-col lg:min-h-0" data-testid="recent-files-card">
          <h2 className="font-heading text-base lg:text-lg font-bold mb-2 lg:mb-3">{t("dash.recent_title")}</h2>
          {recent.length === 0 ? (
            <p className="t-secondary text-sm" data-testid="no-recent-files">{t("dash.no_recent")}</p>
          ) : (
            <ul className="divide-y divide-[var(--border-color)] max-h-64 lg:max-h-none lg:flex-1 lg:min-h-0 overflow-y-auto">
              {recent.map((f, i) => {
                const Icon = KIND_ICONS[f.kind] || FileText;
                return (
                  <li key={f.id} className={i >= 3 ? "hidden lg:block" : ""}>
                    <button
                      onClick={() => openRecent(f)}
                      className="w-full flex items-center gap-3 py-2 lg:py-3 px-2 rounded-lg hover:bg-white/5 transition-colors text-left"
                      data-testid={`recent-file-${f.file_id}`}
                    >
                      <Icon className="h-4 w-4 brand shrink-0" />
                      <span className="text-sm truncate flex-1">{f.name}</span>
                      <span className="text-xs t-secondary">
                        {new Date(f.opened_at).toLocaleDateString(dateLocale(lang), { day: "numeric", month: "short" })}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <FeeDueBanner />
    </div>
  );
}
